import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  FETCH_USER_PERMISSIONS_KEY,
  getUserPermissions,
} from '@/actions/permissionActions'
import { invalidateFetch } from '@/reducers/fetch'
import {
  FETCH_SUBSCRIPTIONS_KEY,
  fetchSubscriptions,
} from '@/reducers/subscription.slice'
import { clearAppliedChange } from './lib/planChange.slice'
import { receiptAnnouncement } from './lib/helpers'
import { AppliedReceipt } from './AppliedReceipt'
import { ScheduledReceipt } from './ScheduledReceipt'
import type { AppliedPlanChange } from './lib/planChange.types'

export interface ResultStepProps {
  result: AppliedPlanChange
}

export const ResultStep = ({ result }: ResultStepProps) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  useEffect(() => {
    // The key already carries a receivedAt from app start and nothing else invalidates it,
    // so without this the refetch short-circuits and /accounts shows the pre-change plan.
    // Permissions too, or features the new plan grants stay locked until a reload.
    dispatch(invalidateFetch(FETCH_SUBSCRIPTIONS_KEY))
    dispatch(fetchSubscriptions())
    dispatch(invalidateFetch(FETCH_USER_PERMISSIONS_KEY))
    dispatch(getUserPermissions())
    // Mount only: the outcome is already in hand, so a re-run refetches for nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Clearing on the way out rather than inside the click: dropping the outcome while
  // this step is still mounted lets the route's own result guard redirect to choose,
  // which beats the navigation below.
  useEffect(
    () => () => {
      dispatch(clearAppliedChange())
    },
    [dispatch]
  )

  // A live region only announces when its content changes, so it mounts empty and the
  // outcome lands on the next commit.
  const [announcement, setAnnouncement] = useState('')
  useEffect(() => {
    setAnnouncement(receiptAnnouncement(result))
  }, [result])

  const handleDone = () => {
    navigate('/accounts')
  }

  return (
    <>
      <div role="status" className="sr-only">
        {announcement}
      </div>
      {result.status === 'applied' ? (
        <AppliedReceipt result={result} onDone={handleDone} />
      ) : (
        <ScheduledReceipt result={result} onDone={handleDone} />
      )}
    </>
  )
}
